/**
 * Top Movers Widget
 * Shows today's top 3 winners and top 3 losers from active positions by daily P&L.
 */

import React, { useMemo } from 'react';
import { formatSignedCurrency } from '../../utils/formatters';

interface TopMoversWidgetProps {
  activePositions: any[];
  loading: boolean;
  onPositionClick: (ticker: string) => void;
}

interface MoverData {
  id: string;
  ticker: string;
  /** Glossary today_pnl (gold.v_position), both legs. */
  todayPnl: number;
}

const TopMoversWidget: React.FC<TopMoversWidgetProps> = ({ activePositions, loading, onPositionClick }) => {
  // Gold v2 phase 3b: one row per position, its server Today's P&L; the widget
  // only orders them (no browser sums or percentages).
  const { winners, losers } = useMemo(() => {
    const movers: MoverData[] = (activePositions ?? [])
      .filter((p: any) => p.todayPnl != null)
      .map((p: any) => ({ id: p.id, ticker: p.ticker, todayPnl: p.todayPnl as number }));
    const w = movers.filter((m) => m.todayPnl > 0).sort((a, b) => b.todayPnl - a.todayPnl).slice(0, 3);
    const l = movers.filter((m) => m.todayPnl < 0).sort((a, b) => a.todayPnl - b.todayPnl).slice(0, 3);
    return { winners: w, losers: l };
  }, [activePositions]);

  if (loading) {
    return (
      <div className="command-tile top-movers-tile">
        <div className="command-tile-header">
          <span className="command-tile-title">Today's Top Movers</span>
        </div>
        <div className="top-movers-empty">Loading positions...</div>
      </div>
    );
  }

  if (!activePositions || activePositions.length === 0) {
    return (
      <div className="command-tile top-movers-tile">
        <div className="command-tile-header">
          <span className="command-tile-title">Today's Top Movers</span>
        </div>
        <div className="top-movers-empty">Add positions to see daily movers</div>
      </div>
    );
  }

  if (winners.length === 0 && losers.length === 0) {
    return (
      <div className="command-tile top-movers-tile">
        <div className="command-tile-header">
          <span className="command-tile-title">Today's Top Movers</span>
        </div>
        <div className="top-movers-empty">No daily movers today</div>
      </div>
    );
  }

  const renderSection = (title: string, items: MoverData[], type: 'winners' | 'losers') => (
    <div className={`top-movers-section top-movers-${type}`}>
      <div className="top-movers-section-title">{title}</div>
      {items.length === 0 ? (
        <div className="top-movers-none">None today</div>
      ) : (
        items.map(m => (
          <div
            key={m.id}
            className="top-mover-row"
            onClick={() => onPositionClick(m.ticker)}
          >
            <span className="top-mover-ticker">{m.ticker}</span>
            <div className="top-mover-pnl-grid">
              <span className={`top-mover-pnl top-mover-total ${m.todayPnl >= 0 ? 'pnl-positive' : 'pnl-negative'}`}>
                {formatSignedCurrency(m.todayPnl)}
              </span>
            </div>
          </div>
        ))
      )}
    </div>
  );

  return (
    <div className="command-tile top-movers-tile">
      <div className="command-tile-header">
        <span className="command-tile-title">Today's Top Movers</span>
        <div className="top-movers-legend">
          <span className="top-movers-legend-item top-movers-legend-total">Today's P&amp;L</span>
        </div>
      </div>
      <div className="top-movers-sections">
        {renderSection('Winners', winners, 'winners')}
        {renderSection('Losers', losers, 'losers')}
      </div>
    </div>
  );
};

export default TopMoversWidget;
